import { useCallback, useState, type MouseEvent } from 'react';

import { useOneButtonStore } from '../../../../store/oneButtonStore';
import hoverServices, {
    hoverJobIdFromPayload,
    isHoverJobAvailable,
    openHoverJobViewer,
} from '../../../../services/oneButtonServices/hoverServices';

import { HoverJobCreatingModal } from './HoverJobCreatingModal';

const CREATE_FAILED_MESSAGE = 'Failed to create Hover Job';

export type HoverJobModalPhase = 'confirm' | 'creating' | 'success' | 'error';

export function useHoverJobAction(opportunityID: string | number | null | undefined) {
    const hoverJobId = useOneButtonStore((s) => s.hoverJobId);
    const theme = useOneButtonStore((s) => s.theme);
    const hoverAvailable = isHoverJobAvailable(hoverJobId);

    const [modalOpen, setModalOpen] = useState(false);
    const [phase, setPhase] = useState<HoverJobModalPhase>('creating');
    const [errorMessage, setErrorMessage] = useState('');
    const [createdJobId, setCreatedJobId] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const closeModal = useCallback(() => {
        setModalOpen(false);
    }, []);

    const createHoverJob = useCallback(async () => {
        if (busy || !opportunityID) return;

        const ownerId = useOneButtonStore.getState().onebuttonCapabilities?.owner_Id ?? '';
        setModalOpen(true);
        setPhase('creating');
        setCreatedJobId(null);
        setErrorMessage('');
        setBusy(true);

        try {
            const res = await hoverServices.createHoverJob(opportunityID, ownerId);
            if (res?.IsSuccessful) {
                let id = hoverJobIdFromPayload(res);
                if (!id) {
                    id = await hoverServices.pollHoverJobId(opportunityID);
                }
                if (id) {
                    useOneButtonStore.getState().setHoverJobId(id);
                    setCreatedJobId(id);
                    setPhase('success');
                    return;
                }
            }

            setPhase('error');
            setErrorMessage(res?.Message || CREATE_FAILED_MESSAGE);
        } catch {
            setPhase('error');
            setErrorMessage(CREATE_FAILED_MESSAGE);
        } finally {
            setBusy(false);
        }
    }, [busy, opportunityID]);

    const promptCreateHoverJob = useCallback(() => {
        if (busy || !opportunityID) return;
        setModalOpen(true);
        setPhase('confirm');
        setCreatedJobId(null);
        setErrorMessage('');
    }, [busy, opportunityID]);

    const handleHoverClick = useCallback(
        (event?: MouseEvent) => {
            event?.preventDefault();
            event?.stopPropagation();

            if (hoverAvailable) {
                openHoverJobViewer(hoverJobId);
                return;
            }

            promptCreateHoverJob();
        },
        [hoverAvailable, hoverJobId, promptCreateHoverJob],
    );

    const modalElement = (
        <HoverJobCreatingModal
            open={modalOpen}
            onClose={closeModal}
            phase={phase}
            jobId={createdJobId}
            errorMessage={errorMessage}
            theme={theme}
            onConfirmCreate={() => void createHoverJob()}
        />
    );

    return {
        hoverAvailable,
        hoverJobId,
        busy,
        handleHoverClick,
        modalElement,
    };
}
